// ObjectiveTaxonomyResolver — pure, config-driven assembly of the objective→lever
// taxonomy stack and module bindings. This REPLACES the hardcoded
// `buildV1LogisticsEbitdaTaxonomyStack` / `listV1LogisticsEbitdaModuleBindings` literals
// in the runtime so logistics and retail (and any future industry) resolve through the
// SAME spine keyed on objectiveMetricId + industryOverlayId.
//
// Boundary (migration correction 1): packages/lib owns ONLY the pure taxonomy contracts,
// validators, and composition. It NEVER reads the filesystem or app config layout — the
// app-side `FileObjectiveTaxonomyResolver` loads + validates the JSON files and hands the
// already-parsed config to `createObjectiveTaxonomyResolver`.
//
// Fail-closed (mirrors the narration resolver, NOT the planner registry's silent skip):
// a requested objective/industry mapping, overlay, binding overlay, or lever family that
// is missing throws — it is never silently dropped.

import type {
  LeverFamily,
  LeverModuleBinding,
  ObjectiveTaxonomyOverlayKind,
  ObjectiveTaxonomyStack,
} from './objective-to-lever-decomposer';

export const OBJECTIVE_TAXONOMY_OVERLAY_FILE_SCHEMA_VERSION =
  'helix_objective_taxonomy_overlay_v1' as const;
export const OBJECTIVE_MODULE_BINDING_OVERLAY_FILE_SCHEMA_VERSION =
  'helix_objective_module_binding_overlay_v1' as const;
export const OBJECTIVE_TAXONOMY_MAP_SCHEMA_VERSION =
  'helix_objective_taxonomy_map_v1' as const;

export type ObjectiveConfigStatus = 'approved' | 'draft' | 'deprecated';

// Governance metadata required on every taxonomy/module-binding/map config file
// (migration correction 2). `approvedBy` alone is not enough — config that drives
// governed dollars must carry an audit trail and a tamper/drift checksum.
export type ObjectiveConfigGovernance = Readonly<{
  status: ObjectiveConfigStatus;
  approvedAt: string;
  approvalRefId: string;
  sourceEvidenceRefs: readonly string[];
  checksum: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}>;

export type ObjectiveTaxonomyOverlayFile = Readonly<{
  schemaVersion: typeof OBJECTIVE_TAXONOMY_OVERLAY_FILE_SCHEMA_VERSION;
  overlayId: string;
  version: string;
  kind: ObjectiveTaxonomyOverlayKind;
  provenance: string;
  approvedBy: string;
  governance: ObjectiveConfigGovernance;
  leverFamilies: readonly LeverFamily[];
}>;

export type ObjectiveModuleBindingOverlayFile = Readonly<{
  schemaVersion: typeof OBJECTIVE_MODULE_BINDING_OVERLAY_FILE_SCHEMA_VERSION;
  overlayId: string;
  version: string;
  provenance: string;
  approvedBy: string;
  governance: ObjectiveConfigGovernance;
  bindings: readonly LeverModuleBinding[];
}>;

// One mapping per (objectiveMetricId, industryOverlayId). It pins the exact stack
// identity (so the resolved stack is byte-equal to the legacy hardcoded one) and the
// CURATED, ORDERED lever-family selection — the runtime never blindly unions all
// families; the map decides which levers serve which objective in which order.
export type ObjectiveTaxonomyMapEntry = Readonly<{
  objectiveMetricId: string;
  industryOverlayId: string | null;
  stackId: string;
  taxonomyVersion: string;
  coreTaxonomyOverlayId: string;
  industryTaxonomyOverlayIds: readonly string[];
  // overlay ids whose bindings are merged into the resolved moduleBindings list
  moduleBindingOverlayIds: readonly string[];
  // exact version strings recorded on stack.moduleBindingOverlays (kept verbatim for
  // byte-equality with the legacy stack, which records a declared version that may
  // differ from the binding overlay's own version)
  moduleBindingStackVersions: readonly string[];
  // curated, ORDERED lever-family ids resolved from core ∪ industry ∪ tenant
  resolvedLeverFamilyIds: readonly string[];
  approvedBy: string;
}>;

export type ObjectiveTaxonomyMap = Readonly<{
  schemaVersion: typeof OBJECTIVE_TAXONOMY_MAP_SCHEMA_VERSION;
  governance: ObjectiveConfigGovernance;
  mappings: readonly ObjectiveTaxonomyMapEntry[];
}>;

// ---------------------------------------------------------------------------
// Tenant COMPANY overlay (#4004 P2, V3/D20) — the company config layer.
// ---------------------------------------------------------------------------
// Company terminology, metric posture, and company-fact gates previously lived
// ONLY inside the tenant narration prompt (chrw_company_profile_v2.json, 12,304
// chars of prompt-embedded business logic — defect D20, and the source of the
// D15 vocabulary conflict). This contract moves those FACTS into governed,
// checksummed config so prompts carry voice only:
//   - metricPreference: the company's primary objective metric + rationale;
//   - terminologyBindings: approved company terms, each either BOUND to a
//     governed metric/lever-family id or carried as an approved term;
//   - companyFactGates: company facts (segment labels, platform names, scale
//     figures) that narration may use ONLY when the named requirement is met.
// Files live in the taxonomy tree's tenant/ subdir (the loader already scans
// it), discriminated from tenant TAXONOMY overlays by schemaVersion.
export const OBJECTIVE_TENANT_OVERLAY_FILE_SCHEMA_VERSION =
  'helix_objective_tenant_overlay_v1' as const;

export const TENANT_TERMINOLOGY_BINDING_KINDS = [
  'metric',
  'lever_family',
  'approved_term',
] as const;

export type TenantTerminologyBindingKind =
  (typeof TENANT_TERMINOLOGY_BINDING_KINDS)[number];

export type TenantTerminologyBinding = Readonly<{
  term: string;
  aliases: readonly string[];
  kind: TenantTerminologyBindingKind;
  // Governed id the term binds to. REQUIRED for kind 'metric' /
  // 'lever_family'; MUST be null for 'approved_term' (an approved term never
  // smuggles an ungoverned id into the vocabulary).
  governedId: string | null;
  usageNote: string;
  // Citation into the source the fact was drained from (file + section).
  sourceRef: string;
}>;

export const TENANT_COMPANY_FACT_GATE_REQUIREMENTS = [
  // The fact may be used only when approved company context supplies it
  // (segment, service, geography, platform labels).
  'approved_company_context',
  // The fact may be used only when an approved numeric binding supplies the
  // exact value (company scale figures).
  'approved_numeric_binding',
] as const;

export type TenantCompanyFactGateRequirement =
  (typeof TENANT_COMPANY_FACT_GATE_REQUIREMENTS)[number];

export type TenantCompanyFactGate = Readonly<{
  gateId: string;
  fact: string;
  requirement: TenantCompanyFactGateRequirement;
  sourceRef: string;
}>;

export type TenantMetricPreference = Readonly<{
  primaryMetricId: string;
  rationale: string;
  sourceRef: string;
}>;

export type ObjectiveTenantOverlayFile = Readonly<{
  schemaVersion: typeof OBJECTIVE_TENANT_OVERLAY_FILE_SCHEMA_VERSION;
  overlayId: string;
  tenantId: string;
  companyName: string;
  // The tenant narration prompt this overlay drained facts from (null when the
  // tenant has no narration profile). Cross-file existence is checked by the
  // app-side conformance spec, not here (lib never reads the filesystem).
  narrationProfileId: string | null;
  version: string;
  provenance: string;
  approvedBy: string;
  governance: ObjectiveConfigGovernance;
  metricPreference: TenantMetricPreference;
  terminologyBindings: readonly TenantTerminologyBinding[];
  companyFactGates: readonly TenantCompanyFactGate[];
}>;

export function objectiveTenantOverlayChecksumPayload(
  file: Omit<ObjectiveTenantOverlayFile, 'governance'> & {
    governance: Omit<ObjectiveConfigGovernance, 'checksum'>;
  },
): unknown {
  return {
    schemaVersion: file.schemaVersion,
    overlayId: file.overlayId,
    tenantId: file.tenantId,
    companyName: file.companyName,
    narrationProfileId: file.narrationProfileId,
    version: file.version,
    provenance: file.provenance,
    approvedBy: file.approvedBy,
    metricPreference: file.metricPreference,
    terminologyBindings: file.terminologyBindings,
    companyFactGates: file.companyFactGates,
  };
}

export function validateObjectiveTenantOverlayFile(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['tenant_overlay_file_object_required'];
  if (value.schemaVersion !== OBJECTIVE_TENANT_OVERLAY_FILE_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  requireNonEmptyString(value.overlayId, errors, 'overlayId');
  requireNonEmptyString(value.tenantId, errors, 'tenantId');
  requireNonEmptyString(value.companyName, errors, 'companyName');
  if (
    !('narrationProfileId' in value) ||
    !(
      value.narrationProfileId === null ||
      (typeof value.narrationProfileId === 'string' &&
        value.narrationProfileId.trim().length > 0)
    )
  ) {
    errors.push('narrationProfileId_invalid');
  }
  requireNonEmptyString(value.version, errors, 'version');
  requireNonEmptyString(value.provenance, errors, 'provenance');
  requireNonEmptyString(value.approvedBy, errors, 'approvedBy');

  const preference = value.metricPreference;
  if (!isRecord(preference)) {
    errors.push('metricPreference_required');
  } else {
    requireNonEmptyString(
      preference.primaryMetricId,
      errors,
      'metricPreference.primaryMetricId',
    );
    requireNonEmptyString(preference.rationale, errors, 'metricPreference.rationale');
    requireNonEmptyString(preference.sourceRef, errors, 'metricPreference.sourceRef');
  }

  if (!Array.isArray(value.terminologyBindings) || value.terminologyBindings.length === 0) {
    errors.push('terminologyBindings_required');
  } else {
    const seenTerms = new Set<string>();
    value.terminologyBindings.forEach((binding, index) => {
      const path = `terminologyBindings[${index}]`;
      if (!isRecord(binding)) {
        errors.push(`${path}_object_required`);
        return;
      }
      requireNonEmptyString(binding.term, errors, `${path}.term`);
      if (typeof binding.term === 'string') {
        const key = binding.term.trim().toLowerCase();
        if (seenTerms.has(key)) errors.push(`${path}.term_duplicate:${key}`);
        seenTerms.add(key);
      }
      if (
        !Array.isArray(binding.aliases) ||
        binding.aliases.some(
          (alias) => typeof alias !== 'string' || alias.trim().length === 0,
        )
      ) {
        errors.push(`${path}.aliases_invalid`);
      }
      const kind = binding.kind;
      if (
        typeof kind !== 'string' ||
        !TENANT_TERMINOLOGY_BINDING_KINDS.includes(kind as TenantTerminologyBindingKind)
      ) {
        errors.push(`${path}.kind_invalid`);
      } else if (kind === 'approved_term') {
        if (binding.governedId !== null) {
          errors.push(`${path}.governedId_must_be_null_for_approved_term`);
        }
      } else if (
        typeof binding.governedId !== 'string' ||
        binding.governedId.trim().length === 0
      ) {
        errors.push(`${path}.governedId_required`);
      }
      requireNonEmptyString(binding.usageNote, errors, `${path}.usageNote`);
      requireNonEmptyString(binding.sourceRef, errors, `${path}.sourceRef`);
    });
  }

  if (!Array.isArray(value.companyFactGates)) {
    errors.push('companyFactGates_required');
  } else {
    const seenGateIds = new Set<string>();
    value.companyFactGates.forEach((gate, index) => {
      const path = `companyFactGates[${index}]`;
      if (!isRecord(gate)) {
        errors.push(`${path}_object_required`);
        return;
      }
      requireNonEmptyString(gate.gateId, errors, `${path}.gateId`);
      if (typeof gate.gateId === 'string') {
        if (seenGateIds.has(gate.gateId)) {
          errors.push(`${path}.gateId_duplicate:${gate.gateId}`);
        }
        seenGateIds.add(gate.gateId);
      }
      requireNonEmptyString(gate.fact, errors, `${path}.fact`);
      if (
        typeof gate.requirement !== 'string' ||
        !TENANT_COMPANY_FACT_GATE_REQUIREMENTS.includes(
          gate.requirement as TenantCompanyFactGateRequirement,
        )
      ) {
        errors.push(`${path}.requirement_invalid`);
      }
      requireNonEmptyString(gate.sourceRef, errors, `${path}.sourceRef`);
    });
  }

  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    verifyChecksum(
      value as unknown as ObjectiveTenantOverlayFile,
      objectiveTenantOverlayChecksumPayload(value as never),
      errors,
    );
  }
  return errors;
}

// Already-parsed config handed to the pure resolver by the app-side file loader.
export type LoadedObjectiveTaxonomyConfig = Readonly<{
  taxonomyOverlays: Readonly<Record<string, ObjectiveTaxonomyOverlayFile>>;
  moduleBindingOverlays: Readonly<Record<string, ObjectiveModuleBindingOverlayFile>>;
  map: ObjectiveTaxonomyMap;
  // Tenant COMPANY overlays keyed by tenantId (#4004 P2, V3/D20). Optional so
  // existing fixture configs (and callers built before the company layer)
  // remain valid: an absent record behaves as "no tenant overlays".
  tenantCompanyOverlays?: Readonly<Record<string, ObjectiveTenantOverlayFile>>;
}>;

export type ObjectiveTaxonomyResolveInput = Readonly<{
  objectiveMetricId: string;
  industryOverlayId: string | null;
  tenantOverlayId?: string | null;
  resolvedAt: string;
  approvedBy?: string;
  tenantOverlayVersion?: string;
}>;

export type ObjectiveTaxonomyResolution = Readonly<{
  taxonomyStack: ObjectiveTaxonomyStack;
  moduleBindings: readonly LeverModuleBinding[];
  mapEntry: ObjectiveTaxonomyMapEntry;
}>;

export interface ObjectiveTaxonomyResolver {
  resolve(input: ObjectiveTaxonomyResolveInput): ObjectiveTaxonomyResolution;
  // Tenant COMPANY overlay accessor (#4004 P2, V3/D20). Returns the governed
  // company overlay for the tenant, or null when the tenant has none — the
  // clean fallback: an unknown tenant runs the base pipeline unpersonalized;
  // it never throws and never invents company facts.
  resolveTenantCompanyOverlay(tenantId: string): ObjectiveTenantOverlayFile | null;
}

// ---------------------------------------------------------------------------
// Checksum — dependency-free stable content hash (drift detector, not crypto).
// ---------------------------------------------------------------------------
// Canonical JSON with sorted keys → FNV-1a 64-bit hex. Lets the validator detect an
// un-reapproved edit to a config file without pulling node:crypto into packages/lib.
export function canonicalizeForChecksum(value: unknown): string {
  return JSON.stringify(sortDeep(value));
}

export function stableConfigChecksum(value: unknown): string {
  const text = canonicalizeForChecksum(value);
  // FNV-1a 64-bit using BigInt for stability across platforms.
  let hash = BigInt('0xcbf29ce484222325');
  const prime = BigInt('0x100000001b3');
  const mask = BigInt('0xffffffffffffffff');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, '0');
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sortDeep(item));
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortDeep(record[key]);
        return acc;
      }, {});
  }
  return value;
}

// The checksum covers the governed content of the file (everything except the
// governance.checksum field itself), so a content edit invalidates the checksum.
export function taxonomyOverlayChecksumPayload(
  file: Omit<ObjectiveTaxonomyOverlayFile, 'governance'> & {
    governance: Omit<ObjectiveConfigGovernance, 'checksum'>;
  },
): unknown {
  return {
    schemaVersion: file.schemaVersion,
    overlayId: file.overlayId,
    version: file.version,
    kind: file.kind,
    provenance: file.provenance,
    approvedBy: file.approvedBy,
    leverFamilies: file.leverFamilies,
  };
}

export function moduleBindingOverlayChecksumPayload(
  file: Omit<ObjectiveModuleBindingOverlayFile, 'governance'> & {
    governance: Omit<ObjectiveConfigGovernance, 'checksum'>;
  },
): unknown {
  return {
    schemaVersion: file.schemaVersion,
    overlayId: file.overlayId,
    version: file.version,
    provenance: file.provenance,
    approvedBy: file.approvedBy,
    bindings: file.bindings,
  };
}

export function taxonomyMapChecksumPayload(
  map: Omit<ObjectiveTaxonomyMap, 'governance'> & {
    governance: Omit<ObjectiveConfigGovernance, 'checksum'>;
  },
): unknown {
  return { schemaVersion: map.schemaVersion, mappings: map.mappings };
}

// ---------------------------------------------------------------------------
// Validation (fail-closed, structural). Returns an error list; empty ⇒ valid.
// ---------------------------------------------------------------------------
const CONFIG_STATUSES: readonly ObjectiveConfigStatus[] = ['approved', 'draft', 'deprecated'];

function validateGovernance(
  governance: unknown,
  errors: string[],
  path: string,
): governance is ObjectiveConfigGovernance {
  if (!isRecord(governance)) {
    errors.push(`${path}_required`);
    return false;
  }
  if (
    typeof governance.status !== 'string' ||
    !CONFIG_STATUSES.includes(governance.status as ObjectiveConfigStatus)
  ) {
    errors.push(`${path}.status_invalid`);
  }
  requireNonEmptyString(governance.approvedAt, errors, `${path}.approvedAt`);
  requireNonEmptyString(governance.approvalRefId, errors, `${path}.approvalRefId`);
  requireNonEmptyString(governance.checksum, errors, `${path}.checksum`);
  requireNonEmptyString(governance.effectiveFrom, errors, `${path}.effectiveFrom`);
  if (
    !('effectiveTo' in governance) ||
    !(governance.effectiveTo === null || typeof governance.effectiveTo === 'string')
  ) {
    errors.push(`${path}.effectiveTo_invalid`);
  }
  if (
    !Array.isArray(governance.sourceEvidenceRefs) ||
    governance.sourceEvidenceRefs.some((ref) => typeof ref !== 'string')
  ) {
    errors.push(`${path}.sourceEvidenceRefs_invalid`);
  }
  return errors.length === 0;
}

export function validateObjectiveTaxonomyOverlayFile(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['overlay_file_object_required'];
  if (value.schemaVersion !== OBJECTIVE_TAXONOMY_OVERLAY_FILE_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  requireNonEmptyString(value.overlayId, errors, 'overlayId');
  requireNonEmptyString(value.version, errors, 'version');
  if (value.kind !== 'core' && value.kind !== 'industry' && value.kind !== 'tenant') {
    errors.push('kind_invalid');
  }
  requireNonEmptyString(value.provenance, errors, 'provenance');
  requireNonEmptyString(value.approvedBy, errors, 'approvedBy');
  if (!Array.isArray(value.leverFamilies) || value.leverFamilies.length === 0) {
    errors.push('leverFamilies_required');
  } else {
    value.leverFamilies.forEach((family, index) =>
      validateLeverFamily(family, errors, `leverFamilies[${index}]`),
    );
  }
  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    verifyChecksum(
      value as unknown as ObjectiveTaxonomyOverlayFile,
      taxonomyOverlayChecksumPayload(value as never),
      errors,
    );
  }
  return errors;
}

export function validateObjectiveModuleBindingOverlayFile(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['module_binding_file_object_required'];
  if (value.schemaVersion !== OBJECTIVE_MODULE_BINDING_OVERLAY_FILE_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  requireNonEmptyString(value.overlayId, errors, 'overlayId');
  requireNonEmptyString(value.version, errors, 'version');
  requireNonEmptyString(value.provenance, errors, 'provenance');
  requireNonEmptyString(value.approvedBy, errors, 'approvedBy');
  if (!Array.isArray(value.bindings) || value.bindings.length === 0) {
    errors.push('bindings_required');
  } else {
    value.bindings.forEach((binding, index) =>
      validateModuleBinding(binding, errors, `bindings[${index}]`),
    );
  }
  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    verifyChecksum(
      value as unknown as ObjectiveModuleBindingOverlayFile,
      moduleBindingOverlayChecksumPayload(value as never),
      errors,
    );
  }
  return errors;
}

export function validateObjectiveTaxonomyMap(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['map_object_required'];
  if (value.schemaVersion !== OBJECTIVE_TAXONOMY_MAP_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  if (!Array.isArray(value.mappings) || value.mappings.length === 0) {
    errors.push('mappings_required');
  } else {
    value.mappings.forEach((entry, index) => {
      const path = `mappings[${index}]`;
      if (!isRecord(entry)) {
        errors.push(`${path}_object_required`);
        return;
      }
      requireNonEmptyString(entry.objectiveMetricId, errors, `${path}.objectiveMetricId`);
      if (
        !(entry.industryOverlayId === null || typeof entry.industryOverlayId === 'string')
      ) {
        errors.push(`${path}.industryOverlayId_invalid`);
      }
      requireNonEmptyString(entry.stackId, errors, `${path}.stackId`);
      requireNonEmptyString(entry.taxonomyVersion, errors, `${path}.taxonomyVersion`);
      requireNonEmptyString(entry.coreTaxonomyOverlayId, errors, `${path}.coreTaxonomyOverlayId`);
      requireStringArray(entry.industryTaxonomyOverlayIds, errors, `${path}.industryTaxonomyOverlayIds`);
      requireStringArray(entry.moduleBindingOverlayIds, errors, `${path}.moduleBindingOverlayIds`);
      requireStringArray(entry.moduleBindingStackVersions, errors, `${path}.moduleBindingStackVersions`);
      if (
        !Array.isArray(entry.resolvedLeverFamilyIds) ||
        entry.resolvedLeverFamilyIds.length === 0 ||
        entry.resolvedLeverFamilyIds.some((id) => typeof id !== 'string')
      ) {
        errors.push(`${path}.resolvedLeverFamilyIds_required`);
      }
      requireNonEmptyString(entry.approvedBy, errors, `${path}.approvedBy`);
    });
  }
  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    verifyChecksum(
      value as unknown as ObjectiveTaxonomyMap,
      taxonomyMapChecksumPayload(value as never),
      errors,
    );
  }
  return errors;
}

function verifyChecksum(
  file: { governance: ObjectiveConfigGovernance },
  payload: unknown,
  errors: string[],
): void {
  const expected = stableConfigChecksum(payload);
  if (file.governance.checksum !== expected) {
    errors.push(`governance.checksum_mismatch:expected_${expected}`);
  }
}

// ---------------------------------------------------------------------------
// Composition + resolver factory.
// ---------------------------------------------------------------------------
export function createObjectiveTaxonomyResolver(
  config: LoadedObjectiveTaxonomyConfig,
): ObjectiveTaxonomyResolver {
  return {
    resolve(input) {
      return resolveObjectiveTaxonomyStack(config, input);
    },
    resolveTenantCompanyOverlay(tenantId) {
      return resolveTenantCompanyOverlay(config, tenantId);
    },
  };
}

// Pure tenant-company-overlay lookup: exact tenantId match or null (clean
// fallback — unknown tenants are NOT an error; they run unpersonalized).
export function resolveTenantCompanyOverlay(
  config: LoadedObjectiveTaxonomyConfig,
  tenantId: string,
): ObjectiveTenantOverlayFile | null {
  return config.tenantCompanyOverlays?.[tenantId] ?? null;
}

export function resolveObjectiveTaxonomyStack(
  config: LoadedObjectiveTaxonomyConfig,
  input: ObjectiveTaxonomyResolveInput,
): ObjectiveTaxonomyResolution {
  const entry = config.map.mappings.find(
    (mapping) =>
      mapping.objectiveMetricId === input.objectiveMetricId &&
      mapping.industryOverlayId === (input.industryOverlayId ?? null),
  );
  if (!entry) {
    throw new Error(
      `objective_taxonomy_unmapped:${input.objectiveMetricId}:${input.industryOverlayId ?? 'none'}`,
    );
  }

  const core = requireOverlay(config, entry.coreTaxonomyOverlayId, 'core');
  const industryOverlays = entry.industryTaxonomyOverlayIds.map((id) =>
    requireOverlay(config, id, 'industry'),
  );
  const tenantOverlay =
    input.tenantOverlayId != null
      ? requireOverlay(config, input.tenantOverlayId, 'tenant')
      : null;

  // Family lookup: core first, then industry, then tenant — later layers override
  // earlier ones on id collision (industry/tenant win over core).
  const familyById = new Map<string, LeverFamily>();
  for (const family of core.leverFamilies) familyById.set(family.id, family);
  for (const overlay of industryOverlays) {
    for (const family of overlay.leverFamilies) familyById.set(family.id, family);
  }
  if (tenantOverlay) {
    for (const family of tenantOverlay.leverFamilies) familyById.set(family.id, family);
  }

  const resolvedLeverFamilies = entry.resolvedLeverFamilyIds.map((id) => {
    const family = familyById.get(id);
    if (!family) {
      throw new Error(`objective_lever_family_missing:${id}`);
    }
    return family;
  });

  const moduleBindings = entry.moduleBindingOverlayIds.flatMap((id) => {
    const overlay = config.moduleBindingOverlays[id];
    if (!overlay) {
      throw new Error(`objective_module_binding_overlay_missing:${id}`);
    }
    return overlay.bindings;
  });

  const taxonomyStack: ObjectiveTaxonomyStack = {
    stackId: entry.stackId,
    taxonomyVersion: entry.taxonomyVersion,
    coreTaxonomyVersion: core.version,
    industryOverlayVersions: industryOverlays.map((overlay) => overlay.version),
    tenantOverlayVersion: input.tenantOverlayVersion,
    taxonomyOverlays: [core.version, ...industryOverlays.map((overlay) => overlay.version)],
    moduleBindingOverlays: [...entry.moduleBindingStackVersions],
    resolvedLeverFamilies,
    resolvedAt: input.resolvedAt,
    approvedBy: input.approvedBy ?? entry.approvedBy,
  };

  return { taxonomyStack, moduleBindings, mapEntry: entry };
}

// ---------------------------------------------------------------------------
// Objective vocabulary (#4004 P2, V1) — objective ids + target metric ids as
// GOVERNED CONFIG. This file relocates the content of the compile-time
// OUTCOME_TARGET_METRIC_IDS table in
// packages/lib/src/ask-helix/contracts/planner-decision.ts into the taxonomy
// config layer (closure relocated, never removed): the config file is the
// AUTHORITY; the lib enum remains as the compile-time projection for backward
// compatibility. The admission spec
// (apps/api/src/ask-helix/objective-taxonomy/objective-vocabulary.spec.ts)
// pins enum ⊆ config (bijection today) and per-objective target-metric
// equality with the legacy table, so a drift between the two fails closed in
// CI rather than silently forking the vocabulary.
//
// NOTE (id systems): targetMetricId values are relocated BYTE-EXACT. Two id
// systems coexist today — colon-delimited versioned taxonomy ids
// ('metric:operating_income:v1') for the finance objectives and bare tokens
// ('margin', 'cost', ...) for the rest. Normalizing them is NOT this layer's
// job (see target-metric-registry.ts "ID CONVENTION"); the vocabulary carries
// current truth verbatim.
// ---------------------------------------------------------------------------

export const OBJECTIVE_VOCABULARY_FILE_SCHEMA_VERSION =
  'helix_objective_vocabulary_v1' as const;

export type ObjectiveVocabularyEntry = Readonly<{
  objectiveId: string;
  targetMetricId: string;
}>;

export type ObjectiveVocabularyFile = Readonly<{
  schemaVersion: typeof OBJECTIVE_VOCABULARY_FILE_SCHEMA_VERSION;
  vocabularyId: string;
  version: string;
  provenance: string;
  approvedBy: string;
  governance: ObjectiveConfigGovernance;
  objectives: readonly ObjectiveVocabularyEntry[];
}>;

export function objectiveVocabularyChecksumPayload(
  file: Omit<ObjectiveVocabularyFile, 'governance'> & {
    governance: Omit<ObjectiveConfigGovernance, 'checksum'>;
  },
): unknown {
  return {
    schemaVersion: file.schemaVersion,
    vocabularyId: file.vocabularyId,
    version: file.version,
    provenance: file.provenance,
    approvedBy: file.approvedBy,
    objectives: file.objectives,
  };
}

export function validateObjectiveVocabularyFile(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['vocabulary_file_object_required'];
  if (value.schemaVersion !== OBJECTIVE_VOCABULARY_FILE_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  requireNonEmptyString(value.vocabularyId, errors, 'vocabularyId');
  requireNonEmptyString(value.version, errors, 'version');
  requireNonEmptyString(value.provenance, errors, 'provenance');
  requireNonEmptyString(value.approvedBy, errors, 'approvedBy');
  if (!Array.isArray(value.objectives) || value.objectives.length === 0) {
    errors.push('objectives_required');
  } else {
    const seen = new Set<string>();
    value.objectives.forEach((entry, index) => {
      const path = `objectives[${index}]`;
      if (!isRecord(entry)) {
        errors.push(`${path}_object_required`);
        return;
      }
      requireNonEmptyString(entry.objectiveId, errors, `${path}.objectiveId`);
      requireNonEmptyString(entry.targetMetricId, errors, `${path}.targetMetricId`);
      if (typeof entry.objectiveId === 'string') {
        if (seen.has(entry.objectiveId)) {
          errors.push(`${path}.objectiveId_duplicate:${entry.objectiveId}`);
        }
        seen.add(entry.objectiveId);
      }
    });
  }
  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    verifyChecksum(
      value as unknown as ObjectiveVocabularyFile,
      objectiveVocabularyChecksumPayload(value as never),
      errors,
    );
  }
  return errors;
}

// Fail-closed accessor: an objective id the governed vocabulary does not carry
// throws — it is never silently mapped to null (that silent-null behavior is
// exactly what the legacy in-code table allowed).
export interface ObjectiveVocabularyResolver {
  resolveObjectiveTargetMetric(objectiveId: string): string;
  listObjectiveIds(): readonly string[];
}

export function createObjectiveVocabularyResolver(
  file: ObjectiveVocabularyFile,
): ObjectiveVocabularyResolver {
  const targetMetricByObjectiveId = new Map(
    file.objectives.map((entry) => [entry.objectiveId, entry.targetMetricId]),
  );
  return {
    resolveObjectiveTargetMetric(objectiveId: string): string {
      const targetMetricId = targetMetricByObjectiveId.get(objectiveId);
      if (!targetMetricId) {
        throw new Error(`objective_vocabulary_unknown_objective:${objectiveId}`);
      }
      return targetMetricId;
    },
    listObjectiveIds(): readonly string[] {
      return file.objectives.map((entry) => entry.objectiveId);
    },
  };
}

function requireOverlay(
  config: LoadedObjectiveTaxonomyConfig,
  overlayId: string,
  expectedKind: ObjectiveTaxonomyOverlayKind,
): ObjectiveTaxonomyOverlayFile {
  const overlay = config.taxonomyOverlays[overlayId];
  if (!overlay) {
    throw new Error(`objective_taxonomy_overlay_missing:${expectedKind}:${overlayId}`);
  }
  if (overlay.kind !== expectedKind) {
    throw new Error(
      `objective_taxonomy_overlay_kind_mismatch:${overlayId}:expected_${expectedKind}:got_${overlay.kind}`,
    );
  }
  return overlay;
}

// ---------------------------------------------------------------------------
// Local validation helpers (kept here so the module stays dependency-free).
// ---------------------------------------------------------------------------
function validateLeverFamily(value: unknown, errors: string[], path: string): void {
  if (!isRecord(value)) {
    errors.push(`${path}_object_required`);
    return;
  }
  requireNonEmptyString(value.id, errors, `${path}.id`);
  requireNonEmptyString(value.label, errors, `${path}.label`);
  if (
    value.branch !== 'revenue' &&
    value.branch !== 'cogs' &&
    value.branch !== 'sga' &&
    value.branch !== 'ops' &&
    value.branch !== 'capital'
  ) {
    errors.push(`${path}.branch_invalid`);
  }
  if (!Array.isArray(value.appliesWhen)) {
    errors.push(`${path}.appliesWhen_required`);
  }
  requireNonEmptyString(value.hypothesisHint, errors, `${path}.hypothesisHint`);
  if (!Array.isArray(value.evidenceNeeds)) {
    errors.push(`${path}.evidenceNeeds_required`);
  }
  if (value.grade !== 'hard_$' && value.grade !== 'soft_$' && value.grade !== 'cash_returns') {
    errors.push(`${path}.grade_invalid`);
  }
  if (value.valueAttribution !== undefined) {
    validateValueAttribution(value.valueAttribution, errors, `${path}.valueAttribution`);
  }
}

function validateValueAttribution(value: unknown, errors: string[], path: string): void {
  if (!isRecord(value)) {
    errors.push(`${path}_object_required`);
    return;
  }
  if (
    value.controllability !== 'self_help' &&
    value.controllability !== 'cycle_sensitive' &&
    value.controllability !== 'mixed' &&
    value.controllability !== 'cash_returns'
  ) {
    errors.push(`${path}.controllability_invalid`);
  }
  validateRiskLevel(value.cycleExposure, errors, `${path}.cycleExposure`);
  validateRiskLevel(
    value.managementActionability,
    errors,
    `${path}.managementActionability`,
  );
  requireNonEmptyString(value.rationale, errors, `${path}.rationale`);
}

function validateRiskLevel(value: unknown, errors: string[], path: string): void {
  if (
    value !== 'low' &&
    value !== 'medium' &&
    value !== 'high' &&
    value !== 'very_high'
  ) {
    errors.push(`${path}_invalid`);
  }
}

function validateModuleBinding(value: unknown, errors: string[], path: string): void {
  if (!isRecord(value)) {
    errors.push(`${path}_object_required`);
    return;
  }
  requireNonEmptyString(value.bindingId, errors, `${path}.bindingId`);
  requireNonEmptyString(value.leverFamilyId, errors, `${path}.leverFamilyId`);
  if (!Array.isArray(value.supportedModuleIds) || value.supportedModuleIds.length === 0) {
    errors.push(`${path}.supportedModuleIds_required`);
  }
  if (!Array.isArray(value.supportedQuestionFamilies)) {
    errors.push(`${path}.supportedQuestionFamilies_required`);
  }
}

function requireNonEmptyString(value: unknown, errors: string[], path: string): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path}_required`);
  }
}

function requireStringArray(value: unknown, errors: string[], path: string): void {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    errors.push(`${path}_required`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
